import {
  type NativeMessage,
  PROTOCOL_VERSION,
  type WebMessage
} from '../protocol/protocol';
import {
  type InstallIframeBridgeOptions,
  installIframeBridge,
  isInsideIframe
} from './iframe-adapter';

/** Функция отписки от события. */
export type UnsubscribeFn = () => void;

/** Обработчик нативного события (native → web). */
export type EventHandler<P = unknown> = (payload: P) => void;

/**
 * Интерфейс window.__nativeBridge. В нативном WebView его инжектит хост; в iframe
 * веб-оболочки тот же объект ставит сервис через installIframeBridge (postMessage-транспорт).
 */
export interface NativeBridgeGlobal {
  receive(msgStr: string): void;
  subscribe(fn: (msgStr: string) => void): UnsubscribeFn;
  send(msgStr: string): void;
}

declare global {
  interface Window {
    __nativeBridge?: NativeBridgeGlobal;
  }
}

interface PendingRpc {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Web-SDK моста: браузерная сторона webview-мини-аппа.
 * Работает только внутри нативного WebView — требует `window.__nativeBridge`,
 * который инжектится хостом через `injectedJavaScriptBeforeContentLoaded`.
 *
 * Жизненный цикл:
 *   bridge.init()          — один раз при старте (шлёт bridge.ready → нативная сторона ответит init)
 *   bridge.on('init', fn)  — получить контекст (тема, локаль, insets, auth, featureFlags)
 *   bridge.send(...)       — fire-and-forget (навигация, аналитика, ui.*)
 *   bridge.request(...)    — RPC с ответом (auth.getStatus, payments.pay и т.д.)
 *   bridge.destroy()       — при размонтировании SPA
 */
class BridgeSdk {
  private readonly handlers = new Map<string, Set<EventHandler>>();
  private readonly pending = new Map<string, PendingRpc>();
  private unsub: UnsubscribeFn | null = null;
  private _ready = false;
  private counter = 0;

  /** `true` после успешного вызова `init()`. */
  get isReady(): boolean {
    return this._ready;
  }

  /**
   * Инициализация: подключается к `__nativeBridge` и отправляет `bridge.ready`.
   * Вызывается один раз — повторные вызовы игнорируются.
   */
  init(options: InstallIframeBridgeOptions = {}): void {
    if (this._ready) return;

    // В нативном WebView __nativeBridge инжектит хост. В iframe веб-оболочки его нет —
    // ставим postMessage-адаптер сами (идемпотентно; сервис может пред-установить с опциями).
    let nb = window.__nativeBridge;
    if (!nb && isInsideIframe()) {
      nb = installIframeBridge(options);
    }
    if (!nb) {
      throw new Error(
        '[WebBridge] window.__nativeBridge not found. ' +
          'Страница должна загружаться в нативном WebView (инжект хоста) ' +
          'или в iframe веб-оболочки (installIframeBridge).'
      );
    }

    this.unsub = nb.subscribe((msgStr) => this._receive(msgStr));
    this._ready = true;
    this._post('bridge.ready', null);
  }

  /**
   * Отправить действие нативной стороне (fire-and-forget).
   * @example bridge.send('navigation.back', null)
   * @example bridge.send('ui.setTabBarVisible', { value: false })
   */
  send(action: string, payload: unknown = null): void {
    this._post(action, payload);
  }

  /**
   * RPC-запрос: отправляет действие и ждёт ответа с `correlationId`.
   * @param timeoutMs — таймаут ожидания (по умолчанию 15 с).
   * @example const status = await bridge.request('auth.getStatus', null)
   */
  request<T = unknown>(
    action: string,
    payload: unknown = null,
    timeoutMs = 15_000
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const id = `r${(++this.counter).toString(36)}_${Date.now().toString(36)}`;
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('Timeout error. Please try again later.'));
      }, timeoutMs);

      this.pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        timer
      });

      const msg: WebMessage = { v: PROTOCOL_VERSION, action, payload, id };
      window.__nativeBridge!.send(JSON.stringify(msg));
    });
  }

  /**
   * Подписаться на событие от нативной стороны.
   * @returns функция отписки
   * @example const off = bridge.on('theme.changed', ({ mode }) => applyTheme(mode))
   */
  on<P = unknown>(event: string, handler: EventHandler<P>): UnsubscribeFn {
    if (!this.handlers.has(event)) this.handlers.set(event, new Set());
    this.handlers.get(event)!.add(handler as EventHandler);
    return () => {
      this.handlers.get(event)?.delete(handler as EventHandler);
    };
  }

  /** Отписаться от всех событий, отменить ожидающие RPC и освободить ресурсы. */
  destroy(): void {
    this.unsub?.();
    this.unsub = null;
    this.pending.forEach((p) => {
      clearTimeout(p.timer);
      p.reject(new Error('[WebBridge] destroyed'));
    });
    this.pending.clear();
    this.handlers.clear();
    this._ready = false;
  }

  private _post(action: string, payload: unknown): void {
    const msg: WebMessage = { v: PROTOCOL_VERSION, action, payload };
    window.__nativeBridge?.send(JSON.stringify(msg));
  }

  private _receive(msgStr: string): void {
    let msg: NativeMessage;
    try {
      msg = JSON.parse(msgStr) as NativeMessage;
    } catch {
      return;
    }

    if (msg.v !== PROTOCOL_VERSION) return;

    // RPC-ответ: correlationId совпадает с id запроса
    if (msg.correlationId !== undefined) {
      const p = this.pending.get(msg.correlationId);
      if (p) {
        clearTimeout(p.timer);
        this.pending.delete(msg.correlationId);
        if (msg.error) {
          p.reject(new Error(msg.error.message));
        } else {
          p.resolve(msg.payload);
        }
        return;
      }
    }

    // Обычное событие — раздаём подписчикам
    this.handlers.get(msg.event)?.forEach((fn) => fn(msg.payload));
  }
}

export { BridgeSdk };

/** Синглтон Web SDK — основная точка входа для web-приложений. */
export const bridge = new BridgeSdk();
